import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * DevModeContext — admin-only Developer Mode toggle + global feature flags.
 *
 *  ┌─────────────────────────────────────────────────────────────────┐
 *  │  state         │ where it lives         │ scope                  │
 *  ├─────────────────────────────────────────────────────────────────┤
 *  │  isAdmin       │ /api/auth/me.is_admin  │ per-user (session)     │
 *  │  devMode       │ localStorage           │ per-browser (admin UI) │
 *  │  flags         │ /api/dev/flags         │ GLOBAL (persisted DB)  │
 *  └─────────────────────────────────────────────────────────────────┘
 *
 *  · `flags` are fetched for every signed-in user (public via auth) so
 *    every client hides tools that are switched off, regardless of role.
 *  · `devMode` is a UI toggle — it never bypasses flag gating on tools,
 *    it only unlocks the extra Dev UI (panel, dev-presets grid, etc.).
 *  · Only admins can flip `devMode`; the toggle is hidden from everyone
 *    else and the Dev Panel refuses to render without both conditions.
 */

const DevModeContext = createContext(null);

const DEV_MODE_STORAGE_KEY = "geneo_dev_mode_v1";

export function DevModeProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [devMode, setDevModeRaw] = useState(() => {
    try { return localStorage.getItem(DEV_MODE_STORAGE_KEY) === "1"; }
    catch { return false; }
  });
  const [flags, setFlags] = useState(null);
  const [flagsLoading, setFlagsLoading] = useState(false);

  // Persist devMode to localStorage; also refuse to keep it on if the user
  // is no longer an admin (e.g., they signed out and back in as someone else).
  const setDevMode = useCallback((next) => {
    const v = !!next && isAdmin;
    setDevModeRaw(v);
    try { localStorage.setItem(DEV_MODE_STORAGE_KEY, v ? "1" : "0"); } catch {}
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin && devMode) setDevMode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const refreshFlags = useCallback(async () => {
    if (!user) { setFlags(null); return; }
    setFlagsLoading(true);
    try {
      const { data } = await api.get("/dev/flags");
      setFlags(data.flags || {});
    } catch {
      // Guests / uninitialized DB — fall back to "everything on" so no tool
      // vanishes just because the flag doc doesn't exist yet.
      setFlags({});
    } finally {
      setFlagsLoading(false);
    }
  }, [user]);

  useEffect(() => { refreshFlags(); }, [refreshFlags]);

  const updateFlags = useCallback(async (nextFlags) => {
    const { data } = await api.put("/dev/flags", { flags: nextFlags });
    setFlags(data.flags || {});
    return data.flags;
  }, []);

  // Feature-gate helper: admins see everything (so they can preview flagged-off
  // tools without flipping the switch). Non-admins strictly follow the flag.
  const isFeatureEnabled = useCallback((key) => {
    if (isAdmin) return true;
    if (!flags) return true; // during first paint, don't hide anything
    return flags[key] !== false;
  }, [isAdmin, flags]);

  const value = {
    isAdmin,
    devMode: isAdmin && devMode,
    setDevMode,
    flags: flags || {},
    flagsLoading,
    refreshFlags,
    updateFlags,
    isFeatureEnabled,
  };

  return <DevModeContext.Provider value={value}>{children}</DevModeContext.Provider>;
}

export const useDevMode = () => {
  const ctx = useContext(DevModeContext);
  if (!ctx) {
    // Safe fallback so components mounted outside the provider don't crash.
    return { isAdmin: false, devMode: false, setDevMode: () => {}, flags: {}, flagsLoading: false, refreshFlags: () => {}, updateFlags: async () => {}, isFeatureEnabled: () => true };
  }
  return ctx;
};
